import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';

interface GridBlockProps {
  className?: string;
  children: React.ReactNode;
  colSpan?: number;
  rowSpan?: number;
  animate?: boolean;
  delay?: number;
  empty?: boolean;
}

export function GridBlock({
  className,
  children,
  colSpan = 1,
  rowSpan = 1,
  animate = true,
  delay = 0,
  empty = false,
}: GridBlockProps) {
  const blockVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: delay * 0.1,
        ease: [0.215, 0.61, 0.355, 1],
      },
    },
  };

  const Component = animate ? motion.div : 'div';

  const gridSpanClass = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
  }[colSpan] || 'col-span-1';

  const borderClass = empty ? '' : 'border border-[#1a1a1a]';
  const negativeMargin = colSpan > 1 ? '-0.5px' : '-1px';

  return (
    <Component
      variants={animate ? blockVariants : undefined}
      initial={animate ? 'hidden' : undefined}
      whileInView={animate ? 'visible' : undefined}
      viewport={{ once: true }}
      style={{ margin: negativeMargin }}
      className={cn(
        'relative bg-[#0D0D0E]',
        gridSpanClass,
        `row-span-${rowSpan}`,
        borderClass,
        className
      )}
    >
      {children}
    </Component>
  );
}

export function GridSection({
  className,
  children,
  cols = 1,
  animate = true
}: {
  className?: string;
  children: React.ReactNode;
  cols?: number;
  gapClass?: string;
  animate?: boolean;
}) {
  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const Component = animate ? motion.div : 'div';

  const gridColsClass = {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4'
  }[cols] || 'md:grid-cols-1';

  return (
    <Component
      variants={animate ? containerVariants : undefined}
      initial={animate ? "hidden" : undefined}
      whileInView={animate ? "visible" : undefined}
      viewport={{ once: true }}
      className={cn(
        'grid grid-cols-1',
        gridColsClass,
        'gap-0',
        className
      )}
    >
      {children}
    </Component>
  );
}